import { useEffect, useState } from 'react';
import { Trophy, Target, Award, Swords, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useHistoryStore } from '@/stores/historyStore';
import { useToast } from '@/hooks/use-toast';
import type { PlayerStats, TeamStats } from '@/types';
import { GAME_MODE_CONFIG } from '@/types';

type StatsTab = 'players' | 'teams';
type PlayerModeFilter = 'all' | '2_player' | '3_player';

export function ResultsPage() {
  const { toast } = useToast();
  const { getAllPlayerStats, getAllTeamStats, error, clearError } =
    useHistoryStore();

  const [playerStats, setPlayerStats] = useState<PlayerStats[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<StatsTab>('players');
  const [playerModeFilter, setPlayerModeFilter] = useState<PlayerModeFilter>('all');
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);

  useEffect(() => {
    async function loadStats() {
      const [players, teams] = await Promise.all([
        getAllPlayerStats(),
        getAllTeamStats(),
      ]);
      setPlayerStats(players);
      setTeamStats(teams);
      setIsLoading(false);
    }
    loadStats();
  }, [getAllPlayerStats, getAllTeamStats]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  if (isLoading) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (playerStats.length === 0 && teamStats.length === 0) {
    return (
      <div className="container py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Complete some matches to see results!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate overall stats
  const totalMatches =
    playerStats.reduce((sum, s) => sum + s.matchesPlayed, 0) / 2; // Divide by 2 since each match has multiple participants
  const totalGames =
    playerStats.reduce((sum, s) => sum + s.gamesPlayed, 0) / 2;
  const topWinner = playerStats.length > 0 ? playerStats[0] : null;
  const topTeam = teamStats.length > 0 ? teamStats[0] : null;

  // Filter and sort player stats based on mode filter
  const getFilteredPlayerStats = (): PlayerStats[] => {
    if (playerModeFilter === 'all') {
      return playerStats;
    }

    // Filter to players who have played in this mode and calculate mode-specific stats
    return playerStats
      .map((player) => {
        const modeStats = player.byMode.find((m) => m.mode === playerModeFilter);
        if (!modeStats || modeStats.matchesPlayed === 0) {
          return null;
        }
        return {
          ...player,
          matchesPlayed: modeStats.matchesPlayed,
          matchesWon: modeStats.matchesWon,
          matchWinRate: modeStats.matchesPlayed > 0
            ? (modeStats.matchesWon / modeStats.matchesPlayed) * 100
            : 0,
          gamesPlayed: modeStats.gamesPlayed,
          gamesWon: modeStats.gamesWon,
          gameWinRate: modeStats.gamesPlayed > 0
            ? (modeStats.gamesWon / modeStats.gamesPlayed) * 100
            : 0,
        };
      })
      .filter((p): p is PlayerStats => p !== null)
      .sort((a, b) => b.matchWinRate - a.matchWinRate);
  };

  const filteredPlayerStats = getFilteredPlayerStats();

  const togglePlayerExpanded = (playerId: string) => {
    setExpandedPlayerId(expandedPlayerId === playerId ? null : playerId);
  };

  const toggleTeamExpanded = (teamId: string) => {
    setExpandedTeamId(expandedTeamId === teamId ? null : teamId);
  };

  return (
    <div className="container py-6 space-y-6">
      {/* Overall Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Swords className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Math.round(totalMatches)}</p>
                <p className="text-xs text-muted-foreground">Total Matches</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary rounded-lg">
                <Target className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Math.round(totalGames)}</p>
                <p className="text-xs text-muted-foreground">Total Games</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Player & Team */}
      <div className="grid grid-cols-1 gap-4">
        {topWinner && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold truncate">
                    {topWinner.playerName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Top Player ({topWinner.matchWinRate.toFixed(0)}% match win
                    rate)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {topTeam && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold truncate">{topTeam.teamName}</p>
                  <p className="text-xs text-muted-foreground">
                    Top Team ({topTeam.matchWinRate.toFixed(0)}% match win rate)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'players' ? 'default' : 'outline'}
          onClick={() => setActiveTab('players')}
        >
          Players
        </Button>
        <Button
          variant={activeTab === 'teams' ? 'default' : 'outline'}
          onClick={() => setActiveTab('teams')}
        >
          Teams
        </Button>
      </div>

      {/* Players Tab Content */}
      {activeTab === 'players' && (
        <div className="space-y-4">
          {playerStats.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No player statistics yet.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mode Filter Tabs */}
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={playerModeFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setPlayerModeFilter('all');
                    setExpandedPlayerId(null);
                  }}
                >
                  All
                </Button>
                <Button
                  variant={playerModeFilter === '2_player' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setPlayerModeFilter('2_player');
                    setExpandedPlayerId(null);
                  }}
                >
                  2 Player
                </Button>
                <Button
                  variant={playerModeFilter === '3_player' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setPlayerModeFilter('3_player');
                    setExpandedPlayerId(null);
                  }}
                >
                  3 Player
                </Button>
              </div>

              {/* Player Leaderboard */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Player Leaderboard
                    {playerModeFilter !== 'all' && (
                      <span className="text-sm font-normal text-muted-foreground">
                        ({playerModeFilter === '2_player' ? '2 Player' : '3 Player'})
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredPlayerStats.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      No players have played in this mode yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {filteredPlayerStats.slice(0, 10).map((player, index) => {
                        const isExpanded = expandedPlayerId === player.playerId;
                        const originalPlayer = playerStats.find(p => p.playerId === player.playerId);

                        return (
                          <div key={player.playerId} className="border rounded-lg">
                            {/* Player Row */}
                            <div
                              className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => togglePlayerExpanded(player.playerId)}
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className={`w-6 text-center font-bold ${
                                    index === 0
                                      ? 'text-yellow-500'
                                      : index === 1
                                        ? 'text-gray-400'
                                        : index === 2
                                          ? 'text-amber-600'
                                          : 'text-muted-foreground'
                                  }`}
                                >
                                  {index + 1}
                                </span>
                                <div>
                                  <p className="font-medium">{player.playerName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.matchesWon}W - {player.matchesPlayed - player.matchesWon}L
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="font-bold">{player.matchWinRate.toFixed(0)}%</p>
                                  <p className="text-xs text-muted-foreground">Win Rate</p>
                                </div>
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && originalPlayer && (
                              <div className="border-t p-4 space-y-4 bg-muted/30">
                                {/* Match Stats */}
                                <div>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {playerModeFilter === 'all' ? 'Overall ' : ''}Match Stats
                                  </p>
                                  <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                      <p className="text-xl font-bold">{player.matchesPlayed}</p>
                                      <p className="text-xs text-muted-foreground">Matches</p>
                                    </div>
                                    <div>
                                      <p className="text-xl font-bold text-green-600">{player.matchesWon}</p>
                                      <p className="text-xs text-muted-foreground">Wins</p>
                                    </div>
                                    <div>
                                      <p className="text-xl font-bold">{player.matchWinRate.toFixed(0)}%</p>
                                      <p className="text-xs text-muted-foreground">Win Rate</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Game Stats */}
                                <div>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {playerModeFilter === 'all' ? 'Overall ' : ''}Game Stats
                                  </p>
                                  <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                      <p className="text-xl font-bold">{player.gamesPlayed}</p>
                                      <p className="text-xs text-muted-foreground">Games</p>
                                    </div>
                                    <div>
                                      <p className="text-xl font-bold text-green-600">{player.gamesWon}</p>
                                      <p className="text-xs text-muted-foreground">Wins</p>
                                    </div>
                                    <div>
                                      <p className="text-xl font-bold">{player.gameWinRate.toFixed(0)}%</p>
                                      <p className="text-xs text-muted-foreground">Win Rate</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Avg Points (only show when filtering by specific mode) */}
                                {playerModeFilter !== 'all' && (
                                  <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">Average per Game:</p>
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                      <div>
                                        <p className="text-xl font-bold">{player.averageRegularPointsPerGame.toFixed(0)}</p>
                                        <p className="text-xs text-muted-foreground">Regular Pts</p>
                                      </div>
                                      <div>
                                        <p className="text-xl font-bold">{player.averageDeclarationPointsPerGame.toFixed(0)}</p>
                                        <p className="text-xs text-muted-foreground">Declarations</p>
                                      </div>
                                      <div>
                                        <p className="text-xl font-bold">{player.averagePointsPerGame.toFixed(0)}</p>
                                        <p className="text-xs text-muted-foreground">Total Pts</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* By Mode (only show in All view) */}
                                {playerModeFilter === 'all' && originalPlayer.byMode.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-sm text-muted-foreground">By Game Mode:</p>
                                    <div className="grid grid-cols-3 gap-2">
                                      {originalPlayer.byMode.map((mode) => (
                                        <div
                                          key={mode.mode}
                                          className="text-center p-2 bg-background rounded-lg border"
                                        >
                                          <p className="text-xs font-medium">
                                            {GAME_MODE_CONFIG[mode.mode].label}
                                          </p>
                                          <p className="text-sm">
                                            {mode.matchesWon}/{mode.matchesPlayed} M
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {mode.gamesWon}/{mode.gamesPlayed} G
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Teams Tab Content */}
      {activeTab === 'teams' && (
        <div className="space-y-4">
          {teamStats.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">
                  No team statistics yet. Play matches with saved teams to track
                  team stats!
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Team Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {teamStats.slice(0, 10).map((team, index) => {
                    const isExpanded = expandedTeamId === team.teamId;

                    return (
                      <div key={team.teamId} className="border rounded-lg">
                        {/* Team Row */}
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => toggleTeamExpanded(team.teamId)}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`w-6 text-center font-bold ${
                                index === 0
                                  ? 'text-yellow-500'
                                  : index === 1
                                    ? 'text-gray-400'
                                    : index === 2
                                      ? 'text-amber-600'
                                      : 'text-muted-foreground'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium">{team.teamName}</p>
                              <p className="text-xs text-muted-foreground">
                                {team.matchesWon}W - {team.matchesPlayed - team.matchesWon}L
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-bold">{team.matchWinRate.toFixed(0)}%</p>
                              <p className="text-xs text-muted-foreground">Win Rate</p>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="border-t p-4 space-y-4 bg-muted/30">
                            {/* Team Members */}
                            <div className="text-sm text-muted-foreground">
                              {team.player1Name} & {team.player2Name}
                            </div>

                            {/* Match Stats */}
                            <div>
                              <p className="text-sm text-muted-foreground mb-2">Match Stats</p>
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <p className="text-xl font-bold">{team.matchesPlayed}</p>
                                  <p className="text-xs text-muted-foreground">Matches</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-green-600">{team.matchesWon}</p>
                                  <p className="text-xs text-muted-foreground">Wins</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{team.matchWinRate.toFixed(0)}%</p>
                                  <p className="text-xs text-muted-foreground">Win Rate</p>
                                </div>
                              </div>
                            </div>

                            {/* Game Stats */}
                            <div>
                              <p className="text-sm text-muted-foreground mb-2">Game Stats</p>
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <p className="text-xl font-bold">{team.gamesPlayed}</p>
                                  <p className="text-xs text-muted-foreground">Games</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-green-600">{team.gamesWon}</p>
                                  <p className="text-xs text-muted-foreground">Wins</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{team.gameWinRate.toFixed(0)}%</p>
                                  <p className="text-xs text-muted-foreground">Win Rate</p>
                                </div>
                              </div>
                            </div>

                            {/* Avg Points */}
                            <div>
                              <p className="text-sm text-muted-foreground mb-2">Average per Game</p>
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <p className="text-xl font-bold">{team.averageRegularPointsPerGame.toFixed(0)}</p>
                                  <p className="text-xs text-muted-foreground">Regular Pts</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{team.averageDeclarationPointsPerGame.toFixed(0)}</p>
                                  <p className="text-xs text-muted-foreground">Declarations</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold">{team.averagePointsPerGame.toFixed(0)}</p>
                                  <p className="text-xs text-muted-foreground">Total Pts</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
